import React, {useState, useEffect} from 'react'
import { Grid, useMediaQuery } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import classNames from 'classnames'
import _ from 'lodash'
import { fitBounds } from 'google-map-react'
import { compareTwoStrings } from 'string-similarity'
import {useFilters} from 'src/common/razzle-platform-components/hooks'
import GoogleMap from 'src/common/razzle-platform-map/components/GoogleMap'
import MapSideList from 'src/common/razzle-platform-map/components/MapSideList'
import FiltersRow from 'src/common/razzle-platform-map/components/product/FiltersRow'
import { DefaultListItem, DefaultSkeletonItem} from 'src/common/razzle-platform-map/components/product/DefaultListItem'
import {DefaultMarkerPreviewItem} from 'src/common/razzle-platform-map/components/product/DefaultMarkerPreviewItem'
import FiltersDialog from 'src/common/razzle-platform-map/components/product/FiltersDialog'
import {DEFAULT_CENTER, DEFAULT_ZOOM} from 'src/common/razzle-platform-map/configs'
import { useMobileDetector } from 'src/common/razzle-platform-components/hooks/mobile-detector'
import { useIntl } from 'src/common/razzle-platform-translate'
import '../styles/helpers.css'
import '../styles/custom.css'
import { productRoutes } from 'src/routes'
import AddressFilter from '../components/filters/AddressFilter'

const useStyles = makeStyles({
    root: {
        '& .list-side': {
            '@media (min-width: 1536px)': {
                flexGrow: 1,
            },
        },
    },
    map: {
        position: (matches) => (matches ? 'relative' : 'sticky'),
        height: (matches) => (matches ? '100vh' : 'calc(100vh - 85px)'),
        top: (matches) => (matches ? '0' : '85px'),
        '@media (min-width: 1536px)': {
            minWidth: '60% !important',
        },
    },
})

const DEFAULT_CENTER_MOBILE = { lat: 25.43945821904415, lng: -5.0516999499999713 }

export default function ProductsMap(props) {

    const { isMobile } = useMobileDetector()
    const {products, pagination} = props

    const { formatMessage } = useIntl()
    const states = []
    const [mapSize, setMapSize] = useState({})
    const [hoveredElement, setHoveredElement] = useState(null)
    const [bounds, setBounds] = useState({})
    const [mapView, setMapView] = useState(false)
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [geoCoder, setGeoCoder] = useState(null)
    const { filters, setFilter, applyFilter, cancelFilter, initializeFilters } = useFilters()
    const [center, setCenter] = useState(isMobile ? DEFAULT_CENTER_MOBILE : DEFAULT_CENTER)
    const [zoom, setZoom] = useState(DEFAULT_ZOOM)
    const [isFilteringByCountry, setIsFilteringByCountry] = useState(!!filters['address.countryCode'])
    const matches = useMediaQuery('(max-width: 1100px)')
    const classes = useStyles(matches)
    useEffect(() => {
        if (!_.isEmpty(mapSize) && _.has(filters, 'marginBounds') && filters.marginBounds) {
            // Remove brackets from marginBounds and split it on the comma
            const marginBounds = filters.marginBounds.replace(/[[\]]/g, '').split(',');
            // The order of the bounds is south west then north east
            const mapFit = fitBounds({
                sw: {
                    lat: marginBounds[0],
                    lng: marginBounds[1],
                },
                ne: {
                    lat: marginBounds[2],
                    lng: marginBounds[3],
                }
            }, { width: mapSize.width, height: mapSize.height })
            setCenter(mapFit.center)
            setZoom(mapFit.zoom)
        }
	}, [mapSize])



    const findBestLocationMatch = (wordToCompare, states) => {
        let bestSimilarity = 0
        let stateBestMatch = null
        wordToCompare && states.forEach(state => {
            const similarity = compareTwoStrings(wordToCompare, state.name)
            if (similarity > bestSimilarity) {
                bestSimilarity = similarity
                stateBestMatch = state
            }
        })
        return stateBestMatch
    }


    const toggleMap = () => {
        // When toggeling the map, in the case of hiding it,
        // we need to find the current center of the map ang get the state from it,
        // so that we can pre add this state to the filter from the next request.
        setMapView(!mapView)
        localStorage.setItem('mapView', !mapView)
    }

    const centerMap = (lat, lng, zoom) => {
        if(zoom){
            setCenter({lat, lng})
            setZoom(zoom)
        }else{
            setCenter({lat, lng})
        }
        if(!lat) {
            setCenter(isMobile ? DEFAULT_CENTER_MOBILE : DEFAULT_CENTER)
            setZoom(DEFAULT_ZOOM)
        }
    }

    const handleOnMarkerHover = (item) => {
        setHoveredElement(item.id)
        const element = document.querySelector(`[itemId='${item.id}']`)
        element && element.scrollIntoView({block: 'nearest', behavior: 'smooth', inline: 'start'})
    }

    const updateBounds = (bounds, center) => {
        if(center){
            setCenter(center)
            setBounds(bounds)
        }else{
            setBounds(bounds)
        }

        if (mapView) {
            setFilter({marginBounds: `[${bounds.sw.lat},${bounds.sw.lng},${bounds.ne.lat},${bounds.ne.lng}]`}, true)
        }
    }

    const handleGeoCoder = (geoCoderClass) => {
        setGeoCoder(new geoCoderClass())
    }

    if(!isMobile) {
        return (
            <Grid className={"products-map-container " + classes.root} container>
                <Grid item xs={12}>
                    <div className={`flex ${matches && mapView ? 'flex-col overflow-hidden' : 'flex-row'}`}>
                        <div className={classNames(mapView ? matches ? 'w-full' : 'w-1/2 ' : 'w-full', 'list-side flex flex-col')}>
                            <Grid item >
                                <div className="px-8 mb-6 mt-8">
                                    <h1 className="font-semibold m-0 text-3xl">{formatMessage({
                                        id: 'Pick your vehicle',
                                        defaultMessage: 'Pick your vehicle',
                                    })}</h1>
                                </div>
                                <FiltersRow
                                    bounds={bounds}
                                    mapView={mapView}
                                    toggleMap={toggleMap}
                                    centerMap={centerMap}
                                    mapSize={mapSize}
                                    specificities={props.specificities}
                                    categories={props.categories}
                                    filters={filters}
                                    setFilter={setFilter}
                                    applyFilter={applyFilter}
                                    cancelFilter={cancelFilter}
                                    extraAvailabilityFilters={props.extraAvailabilityFilters}
                                    isFilteringByCountry={isFilteringByCountry}
                                    setIsFilteringByCountry={setIsFilteringByCountry}
                                />
                            </Grid>
                            <MapSideList
                                visible={true}
                                items={products}
                                pagination={pagination}
                                // inProgress={inProgress}
                                mapView={mapView}
                                toggleMap={toggleMap}
                                hoveredElement={hoveredElement}
                                handleHoveredElement={(id) => setHoveredElement(id)}
                                setFilter={setFilter}
                                listItem={props.listItem || DefaultListItem}
                                itemLoader={props.itemLoader || DefaultSkeletonItem}
                                updateInProgress={props.updateInProgress}
                                itemRoutes={productRoutes}
                            />
                        </div>
                        {mapView && (
                            <div className={classNames(mapView ? matches ? 'w-full h-screen ' : 'w-1/2 ' + classes.map : 'w-0', 'flex products-map-container')}>
                                <GoogleMap
                                    isMobile={isMobile}
                                    items={products}
                                    pagination={pagination}
                                    visible={true}
                                    center={center}
                                    zoom={zoom}
                                    hoveredElement={hoveredElement}
                                    handleOnMarkerHover={handleOnMarkerHover}
                                    handleOnMarkerLeave={() => setHoveredElement(null)}
                                    updateBounds={updateBounds}
                                    getMapSize={(height, width) => setMapSize({height, width})}
                                    handleGeoCoder={handleGeoCoder}
                                    filters={filters}
                                    setFilter={setFilter}
                                    applyFilter={applyFilter}
                                    cancelFilter={cancelFilter}
                                    markerPreviwItem={props.markerPreviwItem || DefaultMarkerPreviewItem}
                                    marker={props.marker}
                                    updateInProgress={props.updateInProgress}
                                    toggleMap={toggleMap}
                                    isFilteringByCountry={isFilteringByCountry}
                                    setIsFilteringByCountry={setIsFilteringByCountry}
                                    itemRoutes={productRoutes}
                                />
                            </div>
                        )}
                    </div>
                </Grid>
            </Grid>
        )
    } else {
        return (
            <div className="overflow-hidden w-full products-map-container" style={{ minHeight: "550px" }} >
                <MapSideList
                    visible={isMobile && !mapView}
                    isMobile={isMobile}
                    items={products}
                    pagination={pagination}
                    bounds={bounds}
                    // inProgress={inProgress}
                    mapView={mapView}
                    handleFilterDialog={() => setFiltersOpen(!filtersOpen)}
                    toggleMap={toggleMap}
                    hoveredElement={hoveredElement}
                    handleHoveredElement={(id) => setHoveredElement(id)}
                    centerMap={centerMap}
                    filters={filters}
                    listItem={props.listItem || DefaultListItem}
                    itemLoader={props.itemLoader || DefaultSkeletonItem}
                    updateInProgress={props.updateInProgress}
                    setFilter={setFilter}
                    itemRoutes={productRoutes}
                />
                {mapView && <GoogleMap
                    visible={isMobile && mapView}
                    isMobile={isMobile}
                    items={products}
                    center={center}
                    bounds={bounds}
                    zoom={zoom}
                    pagination={pagination}
                    mapView={mapView}
                    handleGeoCoder={handleGeoCoder}
                    handleFilterDialog={() => setFiltersOpen(!filtersOpen)}
                    toggleMap={toggleMap}
                    centerMap={centerMap}
                    hoveredElement={hoveredElement}
                    updateBounds={updateBounds}
                    handleHoveredElement={(id) => setHoveredElement(id)}
                    handleOnMarkerHover={() => setHoveredElement(null)}
                    handleOnMarkerLeave={() => setHoveredElement(null)}
                    getMapSize={(height, width) => setMapSize({height, width})}
                    // checkOnDrag={this.checkOnDrag}
                    filters={filters}
                    setFilter={setFilter}
                    applyFilter={applyFilter}
                    cancelFilter={cancelFilter}
                    markerPreviwItem={props.markerPreviwItem || DefaultMarkerPreviewItem}
                    marker={props.marker}
                    updateInProgress={props.updateInProgress}
                    isFilteringByCountry={isFilteringByCountry}
                    setIsFilteringByCountry={setIsFilteringByCountry}
                    itemRoutes={productRoutes}
                />}
                {!filtersOpen && !isFilteringByCountry && (
                    <div className="hidden">
                        <AddressFilter
                            values={filters}
                            centerMap={centerMap}
                            mapSize={mapSize}
                            mapView={mapView}
                            isMobile={true}
                            setFilter={setFilter}
                            applyFilter={applyFilter}
                            isFilteringByCountry={isFilteringByCountry}
                            setIsFilteringByCountry={setIsFilteringByCountry}
                        />
                    </div>
                )}
                {filtersOpen &&
                    <FiltersDialog
                        open={filtersOpen}
                        close={() => setFiltersOpen(false)}
                        bounds={bounds}
                        specificities={props.specificities}
                        categories={props.categories}
                        mapView={mapView}
                        centerMap={centerMap}
                        mapSize={mapSize}
                        filters={filters}
                        setFilter={setFilter}
                        applyFilter={applyFilter}
                        cancelFilter={cancelFilter}
                        initializeFilters={initializeFilters}
                        extraAvailabilityFilters={props.extraAvailabilityFilters}
                        isFilteringByCountry={isFilteringByCountry}
                        setIsFilteringByCountry={setIsFilteringByCountry}
                    />
                }
            </div>
        )
    }
}

